import React from 'react';
import ProductLineTitle from 'components/common/general/ProductLineTitle';
import { EnrollmentType, InstalledProductLine, InstalledRate } from 'types/enrollmentService';
import { convertRateValue } from 'global/utils/convertRateValue';
import clsx from 'clsx';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { InfoButton } from '@insurancemenu/imenu-components-lib/dist/icons';
import colors from '@insurancemenu/imenu-components-lib/dist/utils/colors';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { createStyles } from './styles';
import DateStatus from './DateStatus';
import { effectiveThrough } from '../../utils';

export interface ProductLineDataProps {
  productLineData: InstalledProductLine;
  maxRatesCount: number;
  hasMultiProductsPortfolio: boolean;
  effectiveDate: string;
  showProductLine?: boolean;
}

const InstalledPortfolioCard = ({
  productLineData,
  maxRatesCount,
  hasMultiProductsPortfolio,
  effectiveDate,
  showProductLine = true,
}: ProductLineDataProps): JSX.Element => {
  const { type, id: productLineId, carrierPortfolioName, products, terminationEffectiveDate } = productLineData;
  const isTerminated = type === EnrollmentType.TERMINATED;
  const isPortfolioWithSeveralProducts = products.length > 1;
  const terminationDate = terminationEffectiveDate ? normDateToShortAmericanDate(terminationEffectiveDate) : '';

  // rates list value description
  // maxRatesCount * 2 + 0.8 * (maxRatesCount - 1) + 1.5
  // total items quantity * height of each item + margin bottom for all items except last one * (total items - 1) + padding bottom
  const ratesBlockHeight = maxRatesCount
    ? `minmax(${maxRatesCount * 2 + 0.8 * (maxRatesCount - 1) + 1.5}rem, auto)`
    : 'minmax(max-content, 30%)';

  const classes = createStyles({
    isTerminated,
    productsLength: products.length,
    showProductLine,
    ratesBlockHeight, // property to keep similar height in single product portfolio
    hasMultiProductsPortfolio, // property to align single product portfolios content in all cards
  });

  const effectiveThroughDate = dateFormatters.normDateToShortAmericanDate(effectiveThrough(effectiveDate));

  // Commented out Download button for current card, because it will require something later, but not now
  // const DownloadButton = (
  //   <Button
  //     leftIconProp={<Download />}
  //     type="text"
  //     size="small"
  //     className={classes.downloadButton}
  //     color="custom"
  //     customColor={isTerminated ? colors.red[600] : colors.blue[600]}
  //   >
  //     Download exhibits
  //   </Button>
  // );

  const ratesItems = (imProductId: string, rates: InstalledRate[]): JSX.Element[] => {
    return rates.map(({ rateStructure, currentRate }) => {
      return (
        <li key={`${imProductId}-${rateStructure}`} className={classes.ratesItem}>
          <span>{rateStructure}</span>
          <span>{convertRateValue(currentRate)}</span>
        </li>
      );
    });
  };

  const CardHeader = (
    <div className={classes.productHeader}>
      <DateStatus
        type={isTerminated ? 'TERMINATED' : 'ACTIVE'}
        date={isTerminated ? terminationDate : effectiveThroughDate}
      />
      {showProductLine && <ProductLineTitle productLineId={productLineId} styles={classes.productLineTitle} />}{' '}
    </div>
  );

  const productsList = products.map(({ carrierProductId, carrierProductName, rates }) => {
    const filteredRates = rates.filter(({ currentRate }) => currentRate && currentRate !== 'N/A');

    const RatesContent = (
      <div className={classes.ratesWrapper}>
        {filteredRates.length > 0 ? (
          <ul className={classes.ratesList}>{ratesItems(carrierProductId, filteredRates)}</ul>
        ) : (
          <div className={classes.ratesMessage}>
            <InfoButton width="2rem" height="2rem" color={colors.red[600]} /> <span>Rates are unavailable</span>
          </div>
        )}
      </div>
    );

    if (isPortfolioWithSeveralProducts) {
      return (
        <div key={`${carrierProductId}_${carrierProductName}`} className={classes.multiProductWrapper}>
          <h3 className={classes.productTitle}>{carrierProductName}</h3>
          <p className={classes.ratesTitle}>Current Rates</p>
          {RatesContent}
        </div>
      );
    }

    return (
      <div key={`${carrierProductId}_${carrierProductName}`} className={classes.singleProductWrapper}>
        {CardHeader}
        <h3 className={clsx(classes.productTitle, classes.singleProductPortfolioTitle)}>{carrierProductName}</h3>
        <p className={classes.ratesTitle}>Current Rates</p>
        {RatesContent}
        {/* {DownloadButton} */}
      </div>
    );
  });

  return (
    <div className={clsx(classes.productCard, isPortfolioWithSeveralProducts && classes.multiProductCard)}>
      {isPortfolioWithSeveralProducts ? (
        <>
          {CardHeader}
          <div className={classes.multiProductHeader}>
            <h3 className={clsx(classes.productTitle, classes.multiProductPortfolioTitle)}>{carrierPortfolioName}</h3>
          </div>
          <div className={classes.productsList}>{productsList}</div>
          {/* {DownloadButton} */}
        </>
      ) : (
        productsList
      )}
    </div>
  );
};

export default InstalledPortfolioCard;
